import { Injectable } from '@angular/core';

import { of } from 'rxjs';
import { map, concatMap, withLatestFrom, mergeMap, catchError } from 'rxjs/operators';

import { Store, select } from '@ngrx/store';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { RouterReducerState } from '@ngrx/router-store';

import { RouterStateUrl, ReferenceService } from '@sd-portal/shared/util';

import { State } from '..';
import { AircraftProfileAuditService } from '../../aircraft-profile-audit.service';
import * as actions from './aircraft-profile.actions';

@Injectable()
export class AircraftProfileEffects {
  constructor(
    private readonly aircraftProfileAuditService: AircraftProfileAuditService,
    private readonly referenceService: ReferenceService,
    private readonly actions$: Actions,
    private readonly store: Store<{
      router: RouterReducerState<RouterStateUrl>;
      sharedAudit: State;
    }>
  ) {}

  // Skeleton
  loadAircraftProfileAuditSkeleton$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadAircraftProfileAuditSkeleton),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([_action, state]) => {
        const aircraftProfileId = +state.router.state.params['id'];
        return this.aircraftProfileAuditService.getAircraftProfileSkeleton(aircraftProfileId).pipe(
          map(s => actions.AircraftProfileAuditSkeletonLoaded(s)),
          catchError(err => of(actions.AircraftProfileAuditSkeletonLoadError(err)))
        );
      })
    )
  );

  // Reference types
  loadAircraftRelationType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadAircraftRelationTypes),
      mergeMap(() =>
        this.referenceService.getReferenceItems('AircraftRelationType').pipe(
          map(payload => actions.AircraftRelationTypesLoaded({ payload })),
          catchError(err => of(actions.AircraftRelationTypesLoadError(err)))
        )
      )
    )
  );

  loadAircraftRelationSubType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadAircraftRelationSubTypes),
      mergeMap(() =>
        this.referenceService.getReferenceItems('AircraftRelationSubType').pipe(
          map(payload => actions.AircraftRelationSubTypeLoaded({ payload })),
          catchError(err => of(actions.AircraftRelationSubTypesLoadError(err)))
        )
      )
    )
  );

  loadPostFlightVisibilityType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPostFlightVisibilityTypes),
      mergeMap(() =>
        this.referenceService.getReferenceItemsNoCache('PostFlightVisibilityType', false).pipe(
          map(payload => actions.PostFlightVisibilityTypesLoaded({ payload })),
          catchError(err => of(actions.PostFlightVisibilityTypesLoadError(err)))
        )
      )
    )
  );

  // AircraftProfile
  loadAircraftProfileAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadAircraftProfileAudit),
      mergeMap(action =>
        this.aircraftProfileAuditService.getAircraftProfileAudits(action.aircraftProfileId).pipe(
          map(audits =>
            actions.AircraftProfileAuditLoaded({
              aircraftProfileId: action.aircraftProfileId,
              audits
            })
          ),
          catchError(err => of(actions.AircraftProfileAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageAircraftProfileAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageAircraftProfileAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.aircraftProfile.aircraftProfileAudit.lastAuditLogId;
        }
        return this.aircraftProfileAuditService
          .getAircraftProfileAudits(
            action.aircraftProfileId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.AircraftProfileAuditLoaded({
                aircraftProfileId: action.aircraftProfileId,
                audits
              })
            ),
            catchError(err => of(actions.AircraftProfileAuditLoadError(err)))
          );
      })
    )
  );

  // PostFlightVisibility
  loadPostFlightVisibilityAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPostFlightVisibilityAudit),
      mergeMap(action =>
        this.aircraftProfileAuditService
          .getAircraftPostflightVisibilityAudits(action.postFlightVisibilityId)
          .pipe(
            map(audits =>
              actions.PostFlightVisibilityAuditLoaded({
                postFlightVisibilityId: action.postFlightVisibilityId,
                audits
              })
            ),
            catchError(err => of(actions.PostFlightVisibilityAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPagePostFlightVisibilityAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePostFlightVisibilityAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId =
            state.sharedAudit.aircraftProfile.postFlightVisibilityAudit.lastAuditLogId;
        }
        return this.aircraftProfileAuditService
          .getAircraftPostflightVisibilityAudits(
            action.postFlightVisibilityId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.PostFlightVisibilityAuditLoaded({
                postFlightVisibilityId: action.postFlightVisibilityId,
                audits
              })
            ),
            catchError(err => of(actions.PostFlightVisibilityAuditLoadError(err)))
          );
      })
    )
  );

  // AircraftBillingRate
  loadAircraftBillingRateAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadAircraftBillingRateAudit),
      mergeMap(action =>
        this.aircraftProfileAuditService
          .getAircraftBillingRateAudits(action.aircraftBillingRateId)
          .pipe(
            map(audits =>
              actions.AircraftBillingRateAuditLoaded({
                aircraftBillingRateId: action.aircraftBillingRateId,
                audits
              })
            ),
            catchError(err => of(actions.AircraftBillingRateAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPageAircraftBillingRateAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageAircraftBillingRateAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId =
            state.sharedAudit.aircraftProfile.aircraftBillingRateAudit.lastAuditLogId;
        }
        return this.aircraftProfileAuditService
          .getAircraftBillingRateAudits(
            action.aircraftBillingRateId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.AircraftBillingRateAuditLoaded({
                aircraftBillingRateId: action.aircraftBillingRateId,
                audits
              })
            ),
            catchError(err => of(actions.AircraftBillingRateAuditLoadError(err)))
          );
      })
    )
  );

  // ColorSetting
  loadColorSettingAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadColorSettingAudit),
      mergeMap(action =>
        this.aircraftProfileAuditService.getColorSettingAudits(action.colorSettingId).pipe(
          map(audits =>
            actions.ColorSettingAuditLoaded({
              colorSettingId: action.colorSettingId,
              audits
            })
          ),
          catchError(err => of(actions.ColorSettingAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageColorSettingAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageColorSettingAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.aircraftProfile.colorSettingAudit.lastAuditLogId;
        }
        return this.aircraftProfileAuditService
          .getColorSettingAudits(action.colorSettingId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.ColorSettingAuditLoaded({
                colorSettingId: action.colorSettingId,
                audits
              })
            ),
            catchError(err => of(actions.ColorSettingAuditLoadError(err)))
          );
      })
    )
  );

  // AircraftPerformance
  loadAircraftPerformanceAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadAircraftPerformanceAudit),
      mergeMap(action =>
        this.aircraftProfileAuditService
          .getAircraftPerformanceAudits(action.aircraftPerformanceId)
          .pipe(
            map(audits =>
              actions.AircraftPerformanceAuditLoaded({
                aircraftPerformanceId: action.aircraftPerformanceId,
                audits
              })
            ),
            catchError(err => of(actions.AircraftPerformanceAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPageAircraftPerformanceAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageAircraftPerformanceAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId =
            state.sharedAudit.aircraftProfile.aircraftPerformanceAudit.lastAuditLogId;
        }
        return this.aircraftProfileAuditService
          .getAircraftPerformanceAudits(
            action.aircraftPerformanceId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.AircraftPerformanceAuditLoaded({
                aircraftPerformanceId: action.aircraftPerformanceId,
                audits
              })
            ),
            catchError(err => of(actions.AircraftPerformanceAuditLoadError(err)))
          );
      })
    )
  );

  // AircraftProfileEngine
  loadAircraftProfileEngineAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadAircraftProfileEngineAudit),
      mergeMap(action =>
        this.aircraftProfileAuditService
          .getAircraftProfileEngineAudits(action.aircraftProfileEngineId)
          .pipe(
            map(audits =>
              actions.AircraftProfileEngineAuditLoaded({
                aircraftProfileEngineId: action.aircraftProfileEngineId,
                audits
              })
            ),
            catchError(err => of(actions.AircraftProfileEngineAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPageAircraftProfileEngineAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageAircraftProfileEngineAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId =
            state.sharedAudit.aircraftProfile.aircraftProfileEngineAudit.lastAuditLogId;
        }
        return this.aircraftProfileAuditService
          .getAircraftProfileEngineAudits(
            action.aircraftProfileEngineId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.AircraftProfileEngineAuditLoaded({
                aircraftProfileEngineId: action.aircraftProfileEngineId,
                audits
              })
            ),
            catchError(err => of(actions.AircraftProfileEngineAuditLoadError(err)))
          );
      })
    )
  );

  // EngineAudit
  loadEngineAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadEngineAudit),
      mergeMap(action =>
        this.aircraftProfileAuditService.getEngineAudits(action.engineId).pipe(
          map(audits => actions.EngineAuditLoaded({ engineId: action.engineId, audits })),
          catchError(err => of(actions.EngineAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageEngineAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageEngineAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.aircraftProfile.engineAudit.lastAuditLogId;
        }
        return this.aircraftProfileAuditService
          .getEngineAudits(action.engineId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits => actions.EngineAuditLoaded({ engineId: action.engineId, audits })),
            catchError(err => of(actions.EngineAuditLoadError(err)))
          );
      })
    )
  );

  // AircraftProfileComponent
  loadAircraftProfileComponentAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadAircraftProfileComponentAudit),
      mergeMap(action =>
        this.aircraftProfileAuditService
          .getAircraftProfileComponentAudits(action.aircraftProfileComponentId)
          .pipe(
            map(audits =>
              actions.AircraftProfileComponentAuditLoaded({
                aircraftProfileComponentId: action.aircraftProfileComponentId,
                audits
              })
            ),
            catchError(err => of(actions.AircraftProfileComponentAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPageAircraftProfileComponentAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageAircraftProfileComponentAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId =
            state.sharedAudit.aircraftProfile.aircraftProfileComponentAudit.lastAuditLogId;
        }
        return this.aircraftProfileAuditService
          .getAircraftProfileComponentAudits(
            action.aircraftProfileComponentId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.AircraftProfileComponentAuditLoaded({
                aircraftProfileComponentId: action.aircraftProfileComponentId,
                audits
              })
            ),
            catchError(err => of(actions.AircraftProfileComponentAuditLoadError(err)))
          );
      })
    )
  );

  // ComponentAudit
  loadComponentAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadComponentAudit),
      mergeMap(action =>
        this.aircraftProfileAuditService.getComponentAudits(action.componentId).pipe(
          map(audits =>
            actions.ComponentAuditLoaded({
              componentId: action.componentId,
              audits
            })
          ),
          catchError(err => of(actions.ComponentAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageComponentAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageComponentAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.aircraftProfile.componentAudit.lastAuditLogId;
        }
        return this.aircraftProfileAuditService
          .getComponentAudits(action.componentId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.ComponentAuditLoaded({
                componentId: action.componentId,
                audits
              })
            ),
            catchError(err => of(actions.ComponentAuditLoadError(err)))
          );
      })
    )
  );

  // AircraftRelation
  loadAircraftRelationAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadAircraftRelationAudit),
      mergeMap(action =>
        this.aircraftProfileAuditService.getAircraftRelationAudits(action.aircraftRelationId).pipe(
          map(audits =>
            actions.AircraftRelationAuditLoaded({
              aircraftRelationId: action.aircraftRelationId,
              audits
            })
          ),
          catchError(err => of(actions.AircraftRelationAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageAircraftRelationAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageAircraftRelationAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.aircraftProfile.aircraftRelationAudit.lastAuditLogId;
        }
        return this.aircraftProfileAuditService
          .getAircraftRelationAudits(
            action.aircraftRelationId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.AircraftRelationAuditLoaded({
                aircraftRelationId: action.aircraftRelationId,
                audits
              })
            ),
            catchError(err => of(actions.AircraftRelationAuditLoadError(err)))
          );
      })
    )
  );

  // PhoneNumber
  loadPhoneNumberAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPhoneNumberAudit),
      mergeMap(action =>
        this.aircraftProfileAuditService.getPhoneNumberAudits(action.phoneNumberId).pipe(
          map(audits =>
            actions.PhoneNumberAuditLoaded({
              phoneNumberId: action.phoneNumberId,
              audits
            })
          ),
          catchError(err => of(actions.PhoneNumberAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPagePhoneNumberAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePhoneNumberAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.aircraftProfile.phoneNumberAudit.lastAuditLogId;
        }
        return this.aircraftProfileAuditService
          .getPhoneNumberAudits(action.phoneNumberId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.PhoneNumberAuditLoaded({
                phoneNumberId: action.phoneNumberId,
                audits
              })
            ),
            catchError(err => of(actions.PhoneNumberAuditLoadError(err)))
          );
      })
    )
  );

  // FaxNumber
  loadFaxNumberAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFaxNumberAudit),
      mergeMap(action =>
        this.aircraftProfileAuditService.getPhoneNumberAudits(action.phoneNumberId).pipe(
          map(audits =>
            actions.FaxNumberAuditLoaded({
              phoneNumberId: action.phoneNumberId,
              audits
            })
          ),
          catchError(err => of(actions.FaxNumberAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageFaxNumberAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageFaxNumberAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.aircraftProfile.faxNumberAudit.lastAuditLogId;
        }
        return this.aircraftProfileAuditService
          .getPhoneNumberAudits(action.phoneNumberId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.FaxNumberAuditLoaded({
                phoneNumberId: action.phoneNumberId,
                audits
              })
            ),
            catchError(err => of(actions.FaxNumberAuditLoadError(err)))
          );
      })
    )
  );

  // EmailAddress
  loadEmailAddressAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadEmailAddressAudit),
      mergeMap(action =>
        this.aircraftProfileAuditService.getEmailAddressAudits(action.emailAddressId).pipe(
          map(audits =>
            actions.EmailAddressAuditLoaded({
              emailAddressId: action.emailAddressId,
              audits
            })
          ),
          catchError(err => of(actions.EmailAddressAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageEmailAddressAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageEmailAddressAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.aircraftProfile.emailAddressAudit.lastAuditLogId;
        }
        return this.aircraftProfileAuditService
          .getEmailAddressAudits(action.emailAddressId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.EmailAddressAuditLoaded({
                emailAddressId: action.emailAddressId,
                audits
              })
            ),
            catchError(err => of(actions.EmailAddressAuditLoadError(err)))
          );
      })
    )
  );

  // Address
  loadAddressAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadAddressAudit),
      mergeMap(action =>
        this.aircraftProfileAuditService.getAddressAudits(action.addressId).pipe(
          map(audits => actions.AddressAuditLoaded({ addressId: action.addressId, audits })),
          catchError(err => of(actions.AddressAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageAddressAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageAddressAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.aircraftProfile.addressAudit.lastAuditLogId;
        }
        return this.aircraftProfileAuditService
          .getAddressAudits(action.addressId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits => actions.AddressAuditLoaded({ addressId: action.addressId, audits })),
            catchError(err => of(actions.AddressAuditLoadError(err)))
          );
      })
    )
  );
}
